import React, { useState, useEffect } from 'react';
import { getUserIdFromToken } from '../../../../serverUrl';
import { useAddMessageTemplateMutation, useGetMessaeTemplateQuery, useSendReminderMutation } from '../../lib/apiSlice/communication/communicationSlice.tsx';
import { toast } from 'react-toastify';
// Utility for shadow and transition
const modalShadow = "shadow-2xl transition-all duration-300";
const inputBase =
  "w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400 transition";
const labelBase = "block text-sm font-medium text-gray-700 mb-1";

export default function ReminderModal({
  isOpen,
  onClose,
  initialTemplate = null,
  reminderType,
  landlord = null, 
  apartment = null, 
  house = null, 
}) {
  // get the templates
  const { data: messageTemplates, isLoading, error } = useGetMessaeTemplateQuery()

  const [addTemplate, setAddTemplate] = useState(false);
  const [selectedOption, setSelectedOption] = useState(initialTemplate);
  const [customSubject, setCustomSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [lastSelectedTemplateId, setLastSelectedTemplateId] = useState(null);

  const handleAddTemplate = () => setAddTemplate(!addTemplate);
  const handleOptionSelect = (template) => {
    setSelectedOption(template);
    setLastSelectedTemplateId(template.id);
  };
  const handleComposeNew = () => {
    setSelectedOption(null);
    setCustomSubject('');
    setCustomMessage('');
    setLastSelectedTemplateId(null);
  };

  // Autofill subject/message when a template is selected
  useEffect(() => {
    if (selectedOption && selectedOption.id === lastSelectedTemplateId) {
      setCustomSubject(selectedOption.title || '');
      setCustomMessage(selectedOption.text || '');
    }
  }, [selectedOption, lastSelectedTemplateId]);

  const userId = getUserIdFromToken()
  // post the templates
  const [addSMSTemplate] = useAddMessageTemplateMutation();
  const [sendReminder] = useSendReminderMutation();

  const payloadTemplate = {
    title: customSubject,
    text: customMessage,
    created_by_message_template: userId,
    updated_by_message_template: userId,
  };
  // Handles both sending reminders and adding templates
  const handleSend = async () => {
    try {
      addSMSTemplate(payloadTemplate).unwrap()
      toast.success('Succesfully added the template')
    }
    catch (error) {
      // Handle error (show notification, etc.)
      console.error('Failed to save unit:', error);
    }

    setCustomSubject('');
    setCustomMessage('');
    setAddTemplate(false);
    onClose();

  };

  const handleSendRem = async () => {
    console.log('data is here')
    // Build data and params for sendReminder
    const data = {
      subject: customSubject || selectedOption?.title,
      message: customMessage || selectedOption?.text,
    };
    let params = {};
    // Ensure landlord/apartment is an ID, not an object or Promise
    let landlordId = landlord;
    let apartmentId = apartment;
    let houseId = house;

    if (landlord && typeof landlord === 'object') {
      landlordId = landlord.id || landlord._id || landlord.landlord_id;
    }
    if (apartment && typeof apartment === 'object') {
      apartmentId = apartment.id || apartment._id || apartment.apartment_id;
    }
    if (house && typeof house === 'object') {
      houseId = house.id || house._id || house.house_id;
    }
    
    if (reminderType === 'landlord' && landlordId) {
      params.landlord_id = landlordId;
    } 
    else if (reminderType === 'apartment' && apartmentId) {
      params.apartment_id = apartmentId;
    }
    else if (reminderType === 'house' && houseId) {
      params.house_id = houseId;
    }

    console.log('Sending reminder with:', { data, params });

    try {
      await sendReminder({ data, params }).unwrap();
      toast.success(
        reminderType === 'landlord'
          ? "Reminder sent successfully to all your tenants!"
          : "Reminder sent successfully to all the tenants in the selected property!"
      );
      setCustomSubject('');
      setCustomMessage('');
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Failed to send reminder.");
    }
  }

  // Filter templates by search
  const filteredTemplates = messageTemplates?.results
    ? messageTemplates.results.filter(
      t =>
        t.title.toLowerCase().includes(templateSearch.toLowerCase()) ||
        t.text.toLowerCase().includes(templateSearch.toLowerCase())
    )
    : [];

  // Limit template display to 3, make scrollable if more
  const maxVisibleTemplates = 3;
  const templateListScrollable = filteredTemplates;

  if (!isOpen) return null;

  // Determine modal title and send button text
  let reminderTitle = "Send Reminder";
  let sendButtonText = "Send";
  if (reminderType === "apartment") {
    reminderTitle = "Send Reminder for Apartment";
    sendButtonText = "Send to Apartment";
  } else if (reminderType === "landlord") {
    reminderTitle = "Send Reminder for All Properties";
    sendButtonText = "Send to All Properties";
  }

  // UI logic: If there are templates, show dropdown and autofill, else let user compose
  const showTemplatePanel = !addTemplate && messageTemplates?.results && messageTemplates.results.length > 0;
  const showCompose =
    addTemplate ||
    !messageTemplates?.results ||
    messageTemplates?.results.length === 0 ||
    selectedOption === null;

  // Always show the add template button/partition if there are no templates
  const showAlwaysAddTemplatePartition = !addTemplate && (!messageTemplates?.results || messageTemplates.results.length === 0);

  return (
    <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
      <div
        className={`bg-white rounded-2xl ${modalShadow} w-full max-w-4xl mx-4 sm:mx-auto flex flex-col md:flex-row relative`}
        style={{ minWidth: 500, maxWidth: 1100 }}
      >
        {/* Modal header */}
        <div className="-top-10 right-0 left-0 z-20 absolute flex justify-between items-center bg-blue-600 px-8 py-4 border-b rounded-t-2xl text-white"
          style={{ minHeight: 64 }}>
          <span className="font-semibold text-xl tracking-wide">
            {addTemplate ? "Add Message Template" : reminderTitle}
          </span>
          <button
            onClick={onClose}
            className="font-bold text-white hover:text-red-200 text-3xl transition"
            aria-label="Close"
            style={{ marginLeft: 16, lineHeight: 1 }}
          >
            &times;
          </button>
        </div>
        {/* Add padding-top to avoid header overlap */}
        <div className="flex mt-4 pt-[72px] w-full">
          {/* Template selection panel */}
          {showTemplatePanel && (
            <div className="flex flex-col bg-gray-50 p-0 border-r rounded-bl-2xl w-full md:w-2/5" style={{ maxHeight: 340 }}>
              <div className="p-4 border-b">
                <div className="flex flex-col gap-2">
                  <p className="font-semibold text-blue-700">Message Templates</p>
                  <button
                    className="bg-blue-200 hover:bg-blue-300 py-2 rounded w-full font-semibold text-blue-900 text-sm transition"
                    onClick={handleComposeNew}
                    type="button"
                  >
                    Compose New Message
                  </button>
                  <input
                    className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400 w-full text-sm"
                    type="text"
                    placeholder="Search templates..."
                    value={templateSearch}
                    onChange={e => setTemplateSearch(e.target.value)}
                  />
                </div>
              </div>
              <div
                className={`flex-1 ${templateListScrollable ? "overflow-y-auto" : ""}`}
                style={templateListScrollable ? { minHeight: 210 } : {}}
              >
                {filteredTemplates.length === 0 ? (
                  <div className="p-4 text-gray-500 text-center">No templates found.</div>
                ) : (
                  <ul className="divide-y">
                    {filteredTemplates.map((template) => (
                      <li
                        key={template.id}
                        className={`p-3 cursor-pointer flex flex-col gap-1 transition
                          ${selectedOption && selectedOption.id === template.id
                            ? "bg-blue-100 border-l-4 border-blue-500"
                            : "hover:bg-blue-50"
                          }`}
                        onClick={() => handleOptionSelect(template)}
                      >
                        <span className="font-semibold text-blue-900 truncate">{template.title}</span>
                        <span className="text-gray-600 text-xs truncate">{template.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex flex-col gap-2 p-4 border-t">
                <button
                  className="bg-blue-100 hover:bg-blue-200 py-2 rounded w-full font-semibold text-blue-700 transition"
                  onClick={handleAddTemplate}
                  type="button"
                >
                  Add New Template
                </button>
                {/* Show "Back to Send Reminder" if adding template */}
                {addTemplate && (
                  <button
                    className="bg-gray-200 hover:bg-gray-300 py-2 rounded w-full font-semibold text-gray-700 transition"
                    onClick={handleAddTemplate}
                    type="button"
                  >
                    Back to Send Reminder
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Always show add template partition if no templates and not adding */}
          {showAlwaysAddTemplatePartition && (
            <div className="flex flex-col bg-gray-50 p-0 border-r rounded-bl-2xl w-full md:w-2/5" style={{ maxHeight: 340 }}>
              <div className="p-4 border-b">
                <div className="flex flex-col gap-2">
                  <p className="font-semibold text-blue-700">Message Templates</p>
                  <button
                    className="bg-blue-200 hover:bg-blue-300 py-2 rounded w-full font-semibold text-blue-900 text-sm transition"
                    onClick={handleComposeNew}
                    type="button"
                  >
                    Compose New Message
                  </button>
                  <input
                    className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400 w-full text-sm"
                    type="text"
                    placeholder="Search templates..."
                    value={templateSearch}
                    onChange={e => setTemplateSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex-1 p-4 text-gray-500 text-center">
                No templates found.
              </div>
              <div className="flex flex-col gap-2 p-4 border-t">
                <button
                  className="bg-blue-100 hover:bg-blue-200 py-2 rounded w-full font-semibold text-blue-700 transition"
                  onClick={handleAddTemplate}
                  type="button"
                >
                  Add New Template
                </button>
                {addTemplate && (
                  <button
                    className="bg-gray-200 hover:bg-gray-300 py-2 rounded w-full font-semibold text-gray-700 transition"
                    onClick={handleAddTemplate}
                    type="button"
                  >
                    Back to Send Reminder
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Main content */}
          <div className={`flex-1 flex flex-col ${showTemplatePanel ? "md:p-8 p-4" : "p-6"}`}>
            {/* Show landlord or apartment details */}
            {/* {reminderType === "apartment" && apartment && (
              <div className="bg-blue-50 mb-4 px-3 py-2 rounded text-blue-900 text-sm">
                <div>
                  <span className="font-semibold">Apartment:</span>{" "}
                  {apartment.name || apartment.title || apartment.apartmentName}
                </div>
                {apartment.address && (
                  <div>
                    <span className="font-semibold">Address:</span> {apartment.address}
                  </div>
                )}
              </div>
            )} */}
            {/* {reminderType === "landlord" && landlord && (
              <div className="bg-blue-50 mb-4 px-3 py-2 rounded text-blue-900 text-sm">
                <div>
                  <span className="font-semibold">Landlord:</span>{" "}
                  {landlord.name || landlord.fullName || landlord.email}
                </div>
                {landlord.email && (
                  <div>
                    <span className="font-semibold">Email:</span> {landlord.email}
                  </div>
                )}
              </div>
            )} */}

            {/* Compose message UI */}
            {(showCompose || selectedOption) && (
              <>
                <div className="mb-4">
                  <p className={labelBase}>Subject</p>
                  <input
                    className={inputBase}
                    type="text"
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    placeholder="Enter subject"
                  />
                </div>
                <div className="flex-1 mb-4">
                  <p className={labelBase}>Message</p>
                  <textarea
                    className={inputBase}
                    rows={7}
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Enter your message"
                  />
                </div>
              </>
            )}

            {/* Add Template UI */}
            {addTemplate && (
              <>
                <div className="flex justify-end mt-2">
                  <button
                    className="bg-gray-200 hover:bg-gray-300 shadow px-5 py-2 rounded font-semibold text-gray-700 transition"
                    onClick={handleAddTemplate}
                    type="button"
                  >
                    Back to Send Reminder
                  </button>
                </div>
              </>
            )}

            {/* If no templates and not adding template, show info */}
            {!addTemplate && (!messageTemplates?.results || messageTemplates.results.length === 0) && (
              <div className="bg-blue-50 mb-4 p-3 rounded text-blue-700 text-center">
                No templates available, kindly compose your message below and send.
              </div>
            )}

            {/* Modal footer, always at the bottom */}
            <div className="flex justify-between items-center bg-gray-50 mt-auto py-3 border-t rounded-b-2xl">
              <button
                className="bg-red-500 hover:bg-red-600 shadow px-5 py-2 rounded font-semibold text-white transition"
                onClick={onClose}
              >
                Close
              </button>

              <div className="flex gap-2">
                <button
                  className={`py-2 px-5 rounded font-semibold shadow transition ${addTemplate
                      ? "bg-green-500 hover:bg-green-600 text-white"
                      : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                  onClick={addTemplate ? handleSend : handleSendRem}
                  disabled={!(customSubject.trim() && customMessage.trim())}
                  type="button"
                >
                  {addTemplate ? "Add Template" : sendButtonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

